/**
 * Device contracts (POS.md §18.9). These shapes travel as JSON (catalog from the API, setup backed up on the terminal,
 * fixtures for the Kotlin pos-android), so they stay plain data: no classes, no functions.
 */

/** What a till uses a device for. One device may serve several roles; a role may have several devices. */
export type DeviceRole = 'receipt' | 'label' | 'document' | 'scale_read' | 'scale_labels' | 'cash_drawer' | 'customer_display'

export const DEVICE_ROLES: DeviceRole[] = ['receipt', 'label', 'document', 'scale_read', 'scale_labels', 'cash_drawer', 'customer_display']

export type DeviceType = 'printer' | 'label_printer' | 'scale' | 'drawer' | 'display'

/** How bytes reach the device. `browser` = the system print dialog with rendered HTML. */
export type TransportKind = 'browser' | 'webusb' | 'webserial' | 'imin' | 'http'

/** The device's language. `html` renders for the browser print dialog; `serial_scale` reads weight frames. */
export type ProtocolKind = 'html' | 'escpos' | 'star' | 'tspl' | 'zpl' | 'epl' | 'serial_scale' | 'none'

/** Scale label barcode layout (POS.md §18.8 default: `21` + PLU 5 + grams 5 + EAN-13 check). */
export interface ScaleBarcodeFormat {
  prefix: string
  plu_digits: number
  value: 'weight' | 'price'
  value_digits: number
  /** Decimals in the value: 3 for grams of a kilogram, 2 for a price in taka. */
  value_decimals: number
  check_digit: boolean
}

/** A scale's continuous / polled serial output, read by a pattern with a named `weight` group. */
export interface ScaleFrameFormat {
  baud_rate: number
  data_bits?: 7 | 8
  stop_bits?: 1 | 2
  parity?: 'none' | 'even' | 'odd'
  /** Regular expression source; must contain `(?<weight>…)`, may contain `(?<stable>…)`, `(?<unit>…)`. */
  pattern: string
  /** Text the stable group must equal for a settled reading (e.g. `ST`). */
  stable_value?: string
  unit: 'kg' | 'g' | 'lb'
  /** Sent to ask for a reading, for scales that do not stream (e.g. `W\r`). */
  request?: string
}

/** The import file a label-printing scale takes for its PLU list. */
export interface PluExportFormat {
  extension: 'csv' | 'txt'
  delimiter: string
  header: boolean
  encoding: 'utf-8' | 'ascii'
  /** Columns in order: a field of the PLU row, or a constant. */
  columns: { title: string; field?: PluField; value?: string; pad?: number }[]
}

export type PluField = 'plu_code' | 'name' | 'price' | 'unit_price' | 'barcode' | 'shelf_life_days' | 'department'

/** USB ids used to recognise a plugged-in device. */
export interface UsbMatch {
  vendor_id: number
  product_id?: number
}

/** A catalog row (System Admin, central `device_models`). Supporting a new model of a known protocol = a new row. */
export interface DeviceModel {
  _id: string
  name: string
  brand?: string
  type: DeviceType
  roles: DeviceRole[]
  transports: TransportKind[]
  protocol: ProtocolKind
  match?: { usb?: UsbMatch[] }
  /** Protocol settings, merged over the protocol's defaults (see `PROTOCOL_DEFAULTS`). */
  params?: Record<string, unknown>
  scale?: {
    frame?: ScaleFrameFormat
    barcode_formats?: ScaleBarcodeFormat[]
    plu_export?: PluExportFormat
  }
  status?: 'active' | 'inactive'
}

/** How this till reaches one paired device. */
export interface DeviceHandle {
  usb?: { vendor_id: number; product_id: number; serial_number?: string }
  serial?: { usb_vendor_id?: number; usb_product_id?: number }
  http?: { url: string; flavor: 'epos' | 'raw' }
}

export interface PairedDevice {
  id: string
  model_id: string
  /** Shown to the cashier, e.g. "Front counter printer". */
  name: string
  transport: TransportKind
  handle?: DeviceHandle
  /** Per-till overrides of the model's params (paper width, label size…). */
  params?: Record<string, unknown>
}

/** A till's devices and which role uses which; kept on the device and backed up as `Terminal.device_setup`. */
export interface DeviceSetup {
  devices: PairedDevice[]
  roles: Partial<Record<DeviceRole, string[]>>
  /** Role options: print after every sale, open the drawer on cash. */
  options?: { auto_print?: boolean; open_drawer_on_cash?: boolean }
  updated_at?: string
}
